import { useEffect, useMemo } from "react";
import { COMMERCE_CART_ITEMCOUNT } from "../storage/constants";
import {
  createOrderItems,
  removeCartItems,
  updateOrderItem,
} from "../services/cart.service";
import { useAppContext } from "./useAppContext";
import useToast from "./useToast";

export default function useDonationList({ setReadyCheckout }) {
  const {
    frequency,
    total,
    items,
    setLoadOrderItemList,
    cart,
    openMenu,
    editStateItems,
    setEditStateItems,
    setItems,
    loadingItems,
    setLoadingItems,
  } = useAppContext();

  const ready = useMemo(() => {
    const _total = items
      .reduce((acc, next) => {
        return acc + parseFloat(next.attributes.total_price.number);
      }, 0)
      .toFixed(2);
    return (
      items.every((v) => parseInt(v.attributes.total_price.number) !== 0) &&
      total === `$${_total}`
    );
  }, [items, total]);
  const cartLength = useMemo(() => {
    return cart?.relationships?.order_items.data !== undefined
      ? cart.relationships.order_items.data.length
      : "0";
  }, [cart]);

  function onClickEditItem(item) {
    items.map((_item, index) => {
      // Open the form for the item that has been selected.
      if (_item.id === item.id) {
        setEditStateItems([...editStateItems, item.id]);
      }
    });
  }

  function onClickDeleteItem(item) {
    const notifyDeleteItemError = useToast({
      type: "error",
      id: "deleteItem",
      actionText: "RETRY?",
      action: async () => await onClickDeleteItem(item),
      dismissable: true,
      message: "There was a problem removing a fund from your cart.",
    });

    setLoadingItems([...loadingItems, item.id]);
    const cartId = item.relationships.order_id.data.id;
    return removeCartItems(cartId, [item.id])
      .then(() => {
        setItems(items.filter((i) => i.id !== item.id));
        if (items.length === 1) {
          openMenu();
        }
        return items;
      })
      .catch(() => {
        notifyDeleteItemError();
      })
      .finally(() => {
        setLoadingItems((_loadingItems) =>
          _loadingItems.filter((i) => i !== item.id)
        );
        setLoadOrderItemList(true);
      });
  }

  function onSubmitItem(item, amount) {
    const shouldUpdateOrder = cart?.relationships.order_items?.data.some(
      (i) => i.id === item.id
    );
    const notifyCreateOrderItemError = useToast({
      type: "error",
      id: `${shouldUpdateOrder ? "updateOrderItem" : "createOrderItems"}${
        item.id
      }`,
      actionText: "RETRY?",
      action: async () => await onSubmitItem(item, amount),
      dismissable: true,
      message: shouldUpdateOrder
        ? "There was a problem updating the donation amount."
        : "There was a problem saving the donation amount.",
    });
    if (shouldUpdateOrder) {
      return updateOrderItem(item, amount, frequency)
        .catch((e) => {
          notifyCreateOrderItemError();
        })
        .then(() => {
          return true;
        });
    }
    return createOrderItems({
      amt: amount,
      fund_id: item.id,
      freq: frequency,
    })
      .catch(() => {
        notifyCreateOrderItemError();
      })
      .then(() => {
        return true;
      });
  }

  async function onClickSubmit(isMiniCart, item, amount) {
    await onSubmitItem(item, amount);
    if (isMiniCart) {
      history.push("/give/checkout");
    } else {
      setEditStateItems([]);
    }
  }

  // Update cart length in localStorage
  useEffect(() => {
    localStorage.setItem(COMMERCE_CART_ITEMCOUNT, cartLength);
    // Dispatch Storage Event. We need to access the values from the current window.
    window.dispatchEvent(
      new StorageEvent("storage", {
        key: COMMERCE_CART_ITEMCOUNT,
        newValue: cartLength,
      })
    );
  }, [cartLength]);

  // Sync local ready with parent
  useEffect(() => {
    setReadyCheckout(ready);
  }, [ready]);

  return {
    onClickEditItem,
    onClickDeleteItem,
    frequency,
    editStateItems,
    items,
    setEditStateItems,
    onClickSubmit,
    onSubmitItem,
    loadingItems,
  };
}
